<script setup lang="ts">
import {computed, ref, watch} from "vue";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import LegacyAccess from "@/core/interfaces/Access";
import {TeamTaskComment} from "@/core/interfaces/TeamTask";
import {teamTaskApi} from "@/container";
import {initialsOf} from "@/team-task/taskBoard";
import {PendingMention, mentionQueryAt, parseMentions, toStoredBody} from "@/team-task/lib/mentions";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import TeamTaskAuthorPicker from "@/team-task/components/TeamTaskAuthorPicker.vue";
import {Send} from "@lucide/vue";

const {taskId, members, defaultAuthorId} = defineProps<{
    taskId: number;
    members: LegacyAccess[];
    /** The access point of the session, offered as the initial author. */
    defaultAuthorId: number | null;
}>();

const emits = defineEmits<{ (event: 'posted'): void }>();

const comments = ref<TeamTaskComment[]>([]);
const loading = ref<boolean>(false);
const failure = ref<string | null>(null);
const draft = ref<string>('');
const sending = ref<boolean>(false);
const caret = ref<number>(0);
const input = ref<HTMLTextAreaElement | null>(null);

/** Mentions inserted from the picker, swapped back to tokens on submit. */
const pending = ref<PendingMention[]>([]);

/**
 * Who is writing.
 *
 * A Connect workstation is shared by the whole team, so the session says which
 * pharmacy and never which person: the author is picked at each comment. The
 * session's own access point is merely the opening suggestion.
 */
const authorId = ref<number | null>(defaultAuthorId);

const load = async () => {
    loading.value = true;
    failure.value = null;

    try {
        comments.value = await teamTaskApi().comments(taskId);
    } catch {
        failure.value = "Le fil de discussion n'a pas pu être chargé.";
    } finally {
        loading.value = false;
    }
};

watch(() => taskId, () => {
    comments.value = [];
    draft.value = '';
    pending.value = [];
    authorId.value = defaultAuthorId;
    load();
}, {immediate: true});

const query = computed(() => mentionQueryAt(draft.value, caret.value));

const suggestions = computed(() => {
    if (query.value === null) {
        return [];
    }

    const needle = query.value.trim().toLowerCase();

    return members
        .filter(member => !needle || (member.user?.name ?? '').toLowerCase().includes(needle))
        .slice(0, 6);
});

/** "Commenter en tant que Claire…", as the design phrases it. */
const placeholder = computed(() => {
    const name = members.find(member => member.id === authorId.value)?.user?.name;

    return name ? `Commenter en tant que ${name.split(' ')[0]}…` : 'Choisir qui rédige…';
});

const authorName = (accessPointId: number): string =>
    members.find(member => member.id === accessPointId)?.user?.name ?? 'Membre retiré';

const postedOn = (comment: TeamTaskComment): string =>
    dayjs(comment.created_at).locale('fr').format('D MMM à HH:mm');

const pick = (member: LegacyAccess) => {
    const label = member.user?.name ?? '';
    const before = draft.value.slice(0, caret.value);
    const start = before.lastIndexOf('@');

    if (start === -1) {
        return;
    }

    draft.value = `${draft.value.slice(0, start)}@${label} ${draft.value.slice(caret.value)}`;
    pending.value = [...pending.value, {label, accessPointId: member.id}];
    caret.value = start + label.length + 2;

    input.value?.focus();
};

const send = async () => {
    if (!draft.value.trim() || sending.value || authorId.value === null) {
        return;
    }

    sending.value = true;
    failure.value = null;

    try {
        const comment = await teamTaskApi().addComment(
            taskId,
            toStoredBody(draft.value.trim(), pending.value),
            authorId.value
        );

        comments.value = [...comments.value, comment];
        draft.value = '';
        pending.value = [];
        emits('posted');
    } catch {
        failure.value = "Le commentaire n'a pas pu être publié.";
    } finally {
        sending.value = false;
    }
};
</script>

<template>
  <section class="border-t border-gray-100 pt-3">
    <h3 class="mb-2 text-[11px] font-semibold text-gray-500">Commentaires</h3>

    <BaseFlexSpinner v-if="loading" />

    <p v-else-if="!comments.length" class="py-2 text-[11px] text-gray-400">
      Aucun commentaire pour l'instant.
    </p>

    <ul v-else class="mb-3 max-h-52 space-y-2.5 overflow-y-auto pr-1">
      <li v-for="comment in comments" :key="comment.id" class="flex gap-2">
        <span class="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[9px] font-bold text-primary">
          {{ initialsOf(authorName(comment.author_access_point_id)) }}
        </span>
        <div class="min-w-0 flex-1">
          <p class="text-[10px] text-gray-400">
            <span class="font-semibold text-gray-600">{{ authorName(comment.author_access_point_id) }}</span>
            · {{ postedOn(comment) }}
          </p>
          <p class="whitespace-pre-wrap break-words text-xs text-gray-700">
            <template v-for="(segment, index) in parseMentions(comment.body, members)" :key="index">
              <span
                v-if="segment.accessPointId !== null"
                class="rounded px-0.5 font-semibold"
                :class="segment.unknown ? 'bg-gray-100 text-gray-400 line-through' : 'bg-primary/10 text-primary'"
              >{{ segment.text }}</span>
              <template v-else>{{ segment.text }}</template>
            </template>
          </p>
        </div>
      </li>
    </ul>

    <p v-if="failure" class="mb-2 text-[11px] text-rose-600">{{ failure }}</p>

    <div class="relative">
      <div v-if="suggestions.length" class="absolute bottom-full mb-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
        <button
          v-for="member in suggestions"
          :key="member.id"
          class="flex w-full items-center gap-2 px-2 py-1.5 text-left text-[11px] hover:bg-gray-50"
          @click="pick(member)"
        >
          <span class="flex size-5 items-center justify-center rounded-full bg-primary/10 text-[9px] font-bold text-primary">
            {{ initialsOf(member.user?.name) }}
          </span>
          {{ member.user?.name }}
        </button>
      </div>

      <div class="flex items-center gap-2 rounded-2xl border border-gray-200 bg-gray-50/70 p-1.5">
        <TeamTaskAuthorPicker v-model="authorId" :members="members" />
        <textarea
          ref="input"
          v-model="draft"
          rows="1"
          :placeholder="placeholder"
          class="flex-1 resize-none bg-transparent py-1.5 text-xs leading-snug focus:outline-none"
          @keyup="caret = ($event.target as HTMLTextAreaElement).selectionStart ?? 0"
          @click="caret = ($event.target as HTMLTextAreaElement).selectionStart ?? 0"
        />
        <button
          class="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary text-white transition disabled:opacity-40"
          :disabled="!draft.trim() || sending || authorId === null"
          title="Publier"
          @click="send"
        >
          <Send class="size-4" />
        </button>
      </div>
    </div>
  </section>
</template>
